import React, { useState, useMemo } from "react";
import classNames from "classnames";
import styles from "./index.module.css";

interface Props {
  containerClassName?: string; // mainly for applying margin or max-width
  className?: string;
  style?: React.CSSProperties; // usually for specific max-width, min-width
  value: any;
  placeholder?: string;
  onChange: (val: string) => void;
  onKeyEnter?: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  id?: string;
  prepend?: React.ReactNode;
  prependClassName?: string; // mainly to adjust padding for prepend component
  append?: React.ReactNode;
  appendClassName?: string;
  type?: string;
  hint?: string; // *hint will be hidden if error is shown
  error?: boolean | string;
  label?: string;
  maxLength?: number;
  showLengthIndicator?: boolean;
}

export const LineInput = ({
  containerClassName,
  className,
  style,
  value,
  placeholder,
  onChange,
  onKeyEnter,
  disabled,
  autoFocus,
  id,
  prepend,
  prependClassName,
  append,
  appendClassName,
  type,
  hint,
  error,
  label,
  maxLength,
  showLengthIndicator,
  ...rest
}: Props) => {
  const [, setIsFocused] = useState<boolean>(false);

  const shouldShowLengthIndicator = useMemo(
    () => maxLength > 0 && showLengthIndicator,
    [maxLength, showLengthIndicator]
  );
  const shouldShowErrorMessage = useMemo(
    () => !!(typeof error === "string" && error.length),
    [error]
  );
  const shouldShowHint = useMemo(() => hint?.length, [hint]);

  const inputPropsClass = {
    [styles.inputDisabled]: disabled,
    [styles.inputWithPrepend]: prepend,
    [styles.inputError]: error,
  };

  const handleKeyEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.code === "Enter") {
      onKeyEnter();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (maxLength && e.target.value.length > maxLength) {
      return;
    }
    onChange(e.target.value);
  };

  const renderHintOrError = () => {
    if (shouldShowErrorMessage) {
      return (
        <span className="float-left text-caption text-error">{error}</span>
      );
    }

    if (shouldShowHint) {
      return (
        <span className="float-left text-caption text-secondary-gray">
          {hint}
        </span>
      );
    }

    return null;
  };

  const renderLengthIndicator = () => {
    if (shouldShowLengthIndicator) {
      return (
        <span className="float-right text-caption">
          {value?.length} / {maxLength}
        </span>
      );
    }

    return null;
  };

  return (
    <div className={classNames(styles.inputContainer, containerClassName)}>
      {label && (
        <label
          htmlFor={label}
          className={classNames(styles.inputLabel, {
            "text-error": error,
          })}
        >
          {label}
        </label>
      )}
      <div className={styles.inputWrapper} id={id} data-testid={id}>
        {prepend && (
          <div className={classNames(styles.inputPrepend, prependClassName)}>
            {prepend}
          </div>
        )}
        <input
          type={type}
          className={classNames(styles.input, inputPropsClass, className)}
          style={style}
          value={value}
          placeholder={placeholder}
          onChange={handleChange}
          onKeyDown={handleKeyEnter}
          disabled={disabled}
          autoFocus={autoFocus}
          id={label ?? id}
          maxLength={maxLength}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          {...rest}
        />
        {append && <div className={classNames(appendClassName)}>{append}</div>}
      </div>
      {(shouldShowLengthIndicator ||
        shouldShowHint ||
        shouldShowErrorMessage) && (
        <div className="w-full h-3.5 mt-1.5">
          {renderHintOrError()}
          {renderLengthIndicator()}
        </div>
      )}
    </div>
  );
};

LineInput.defaultProps = {
  onKeyEnter: () => {},
  type: "text",
  showLengthIndicator: false,
};
